import React, { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, usePublicClient, useWalletClient } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatUnits, parseUnits, encodeAbiParameters, parseAbiParameters } from 'viem';
import { 
  AlertCircle, CheckCircle, Clock, ArrowRight, Copy, ExternalLink, 
  RefreshCw, Loader2, AlertTriangle, Wallet, Calendar, Users, Gift,
  Play, Send, RotateCcw, Banknote, Shield, XCircle, Rocket, Upload, Timer,
  Search, CheckCheck, Twitter, Youtube, HelpCircle, FileJson, FileSpreadsheet, Download,
  QrCode, Trash2, Eye, Settings2, LogOut, Video, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { formatNumber } from '../lib/utils';
import { TREASURY_ADDRESS } from '../config/wagmi';
import ScoringConfigV2 from '../components/admin/ScoringConfigV2';
import { ScoutSettingsPanel } from '../components/admin/ScoutSettingsPanel';
import { useAdminAuth } from '../hooks/useAdminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use TREASURY_ADDRESS from config instead of hardcoding
const ADMIN_TREASURY = TREASURY_ADDRESS;

// Countdown Timer Component
const CountdownTimer = ({ targetDate, label, variant = 'default' }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const target = new Date(targetDate).getTime();
      const now = new Date().getTime();
      const difference = target - now;
      
      if (difference <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };
    
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearInterval(timer);
  }, [targetDate]);
  
  if (isExpired) {
    return (
      <div className="flex items-center gap-2 text-amber-600 mt-2">
        <Timer className="w-4 h-4" />
        <span className="text-sm font-medium">Time's up! Ready to process.</span>
      </div>
    );
  }
  
  const bgColor = variant === 'claim' ? 'bg-[#f0b90b]/10' : 'bg-white/50';
  const textColor = variant === 'claim' ? 'text-[#f0b90b]' : 'text-green-700';
  
  return (
    <div className={`${bgColor} rounded-lg p-3 mt-2`}>
      <div className="flex items-center gap-2 mb-2">
        <Timer className={`w-4 h-4 ${textColor}`} />
        <span className={`text-sm font-medium ${textColor}`}>{label}</span>
      </div>
      <div className="flex gap-2">
        {[
          { value: timeLeft.days, label: 'd' },
          { value: timeLeft.hours, label: 'h' },
          { value: timeLeft.minutes, label: 'm' },
          { value: timeLeft.seconds, label: 's' }
        ].map((item, idx) => (
          <div key={idx} className="flex items-center">
            <div className={`${variant === 'claim' ? 'bg-[#f0b90b]' : 'bg-green-600'} text-white px-2 py-1 rounded font-mono font-bold text-lg min-w-[40px] text-center`}>
              {String(item.value).padStart(2, '0')}
            </div>
            <span className="text-xs text-gray-500 ml-1">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
const TIP_TOKEN_ADDRESS = process.env.REACT_APP_TIP_TOKEN_ADDRESS || '0x45D8e90deFB7a88C3e5584043205da5a90a43A34';

const ERC20_ABI = [
  {
    inputs: [{ name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    name: 'approve',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }],
    name: 'transfer',
    outputs: [{ name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
];

const DISTRIBUTOR_ABI = [
  {
    inputs: [
      { name: '_merkleRoot', type: 'bytes32' },
      { name: '_claimDeadline', type: 'uint256' },
      { name: '_treasury', type: 'address' },
      { name: '_tipToken', type: 'address' },
      { name: '_epochId', type: 'uint256' }
    ],
    stateMutability: 'nonpayable',
    type: 'constructor'
  },
  {
    inputs: [],
    name: 'returnUnclaimedToTreasury',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [],
    name: 'getInfo',
    outputs: [
      { name: '_merkleRoot', type: 'bytes32' },
      { name: '_claimDeadline', type: 'uint256' },
      { name: '_treasury', type: 'address' },
      { name: '_tipToken', type: 'address' },
      { name: '_epochId', type: 'uint256' },
      { name: '_balance', type: 'uint256' }
    ],
    stateMutability: 'view',
    type: 'function'
  }
];

// Distributor bytecode (compiled from Solidity)
const DISTRIBUTOR_BYTECODE = '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';

export const SecretAdminPage = () => {
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  
  // Admin authentication
  const { 
    isAuthenticated, 
    isLoading: authLoading, 
    error: authError,
    isAdminWallet,
    login: adminLogin,
    logout: adminLogout,
    getAuthHeaders,
    getTimeRemaining,
    clearError: clearAuthError
  } = useAdminAuth();
  
  const [activeTab, setActiveTab] = useState('epochs');
  const [loading, setLoading] = useState(true);
  
  // Settings state
  const [tipPrice, setTipPrice] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Epoch state
  const [epochs, setEpochs] = useState([]);
  const [stats, setStats] = useState(null);
  const [treasuryBalance, setTreasuryBalance] = useState('0');
  const [balanceLoading, setBalanceLoading] = useState(true);
  
  // Verification state
  const [queue, setQueue] = useState([]);
  const [filteredQueue, setFilteredQueue] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [checking, setChecking] = useState(null);  // YouTube check state
  const [platformFilter, setPlatformFilter] = useState('all');
  
  // Deployment state
  const [deploying, setDeploying] = useState(false);
  const [funding, setFunding] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [creatingEpoch, setCreatingEpoch] = useState(false);
  
  // Two-phase finalization state
  const [computing, setComputing] = useState(false);
  const [locking, setLocking] = useState(false);
  const [finalizationStatus, setFinalizationStatus] = useState({});
  
  // Manual distributor address state
  const [manualDistributorAddress, setManualDistributorAddress] = useState('');
  const [settingDistributor, setSettingDistributor] = useState(false);
  const [serverDeploying, setServerDeploying] = useState(false);
  const [deployerInfo, setDeployerInfo] = useState(null);
  
  // Distributor deployment selector state
  const [selectedDeployEpochId, setSelectedDeployEpochId] = useState('');
  
  // Epochs display state - show only 3 by default
  const [showAllEpochs, setShowAllEpochs] = useState(false);
  const EPOCHS_TO_SHOW = 3;
  
  // Old contracts state
  const [oldContracts, setOldContracts] = useState([]);
  const [oldContractsOpen, setOldContractsOpen] = useState(false);
  const [returningFunds, setReturningFunds] = useState(null);
  
  // Alerts state
  const [alerts, setAlerts] = useState([]);
  const [activeEpoch, setActiveEpoch] = useState(null);
  
  // Scheduler state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // QR Admin state
  const [qrStats, setQrStats] = useState(null);
  const [qrList, setQrList] = useState([]);
  const [qrConfig, setQrConfig] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrWalletDebug, setQrWalletDebug] = useState(null);
  const [debugWallet, setDebugWallet] = useState('');
  const [savingQrConfig, setSavingQrConfig] = useState(false);
  const [revokingQr, setRevokingQr] = useState(null);
  
  // Scoring settings state
  const [scoringSettings, setScoringSettings] = useState({
    maxPointsPerWallet: 1000,
    tipperPoolRatio: 0.8,
    creatorPoolRatio: 0.2
    // sybilPenalty removed (2026-03-01) - Sybil V1 deprecated
  });
  
  // Scoring Info state (kept for other functionality)
  const [scoringInfo, setScoringInfo] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Data Integrity Panel state (replaces legacy Sybil V1 controls)
  const [dataIntegrity, setDataIntegrity] = useState(null);
  const [dataIntegrityLoading, setDataIntegrityLoading] = useState(false);
  
  // Admin Settings state (for Settings tab)
  const [adminSettings, setAdminSettings] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [settingUpWallet, setSettingUpWallet] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // V2 Epoch Debug Info (flow path, deployment readiness)
  const [epochDebugInfo, setEpochDebugInfo] = useState({});
  const [v2CutoffEpoch, setV2CutoffEpoch] = useState(12);
  
  const { writeContract, data: hash, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash });

  const isTreasuryWallet = address?.toLowerCase() === TREASURY_ADDRESS.toLowerCase();

  const fetchTreasuryBalance = async () => {
    if (!publicClient) {
      setBalanceLoading(false);
      return;
    }
    try {
      // Fetch native BNB balance (treasury receives BNB from token swaps)
      const balance = await publicClient.getBalance({
        address: TREASURY_ADDRESS,
      });
      setTreasuryBalance(formatUnits(balance, 18));
    } catch (error) {
      console.error('Error fetching treasury balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  // Silent refresh: re-fetches data without showing the full-page loading spinner
  const silentRefresh = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const epochsStatusRes = await axios.get(`${BACKEND_URL}/api/epochs/status`);
      setEpochs(epochsStatusRes.data.epochs || []);
      setAlerts(epochsStatusRes.data.alerts || []);
      setActiveEpoch(epochsStatusRes.data.activeEpoch);
      if (epochsStatusRes.data.epochs?.length > 0) {
        const latestEpoch = epochsStatusRes.data.epochs[0];
        setStats({
          totalEpochs: epochsStatusRes.data.epochs.length,
          totalDistributed: epochsStatusRes.data.epochs
            .filter(e => e.status === 'CLAIMABLE' || e.status === 'COMPLETED')
            .reduce((sum, e) => sum + parseFloat(e.totalRewardPool || 0), 0),
          currentStatus: latestEpoch.status,
          participants: latestEpoch.totalParticipants || 0
        });
      }
      const verifyRes = await axios.get(`${BACKEND_URL}/api/admin/verification/queue`, { headers: authHeaders });
      const sorted = (verifyRes.data.pending || []).sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
      setQueue(sorted);
      try {
        const priceRes = await axios.get(`${BACKEND_URL}/api/tip-price`);
        if (priceRes.data?.price) { setCurrentPrice(priceRes.data.price); setTipPrice(priceRes.data.price.toString()); }
      } catch (e) {}
      try {
        const [settingsRes, infoRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/scoring-settings`, { headers: authHeaders }),
          axios.get(`${BACKEND_URL}/api/scoring-info`)
        ]);
        setScoringSettings(settingsRes.data);
        setScoringInfo(infoRes.data);
      } catch (e) {}
      try {
        const oldRes = await axios.get(`${BACKEND_URL}/api/admin/old-contracts`, { headers: authHeaders });
        setOldContracts(oldRes.data.contracts || []);
      } catch (e) {}
      await loadQrData();
      fetchTreasuryBalance();
    } catch (error) {
      console.error('Silent refresh error:', error);
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await silentRefresh();
    setRefreshing(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
      fetchTreasuryBalance();
      // Auto-refresh every 5 minutes (silent — no loading spinner)
      const interval = setInterval(() => {
        silentRefresh();
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Transaction successful!');
      setFunding(false);  // Reset funding state
      loadAllData();
      fetchTreasuryBalance();
    }
  }, [isSuccess]);

  // Filter queue when platform filter changes
  useEffect(() => {
    if (platformFilter === 'all') {
      setFilteredQueue(queue);
    } else {
      setFilteredQueue(queue.filter(item => item.platform === platformFilter));
    }
  }, [queue, platformFilter]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Get auth headers for admin calls
      const authHeaders = getAuthHeaders();
      
      // Load epochs with status alerts
      const epochsStatusRes = await axios.get(`${BACKEND_URL}/api/epochs/status`);
      setEpochs(epochsStatusRes.data.epochs || []);
      setAlerts(epochsStatusRes.data.alerts || []);
      setActiveEpoch(epochsStatusRes.data.activeEpoch);
      
      if (epochsStatusRes.data.epochs?.length > 0) {
        const latestEpoch = epochsStatusRes.data.epochs[0];
        setStats({
          totalEpochs: epochsStatusRes.data.epochs.length,
          totalDistributed: epochsStatusRes.data.epochs
            .filter(e => e.status === 'CLAIMABLE' || e.status === 'COMPLETED')
            .reduce((sum, e) => sum + parseFloat(e.totalRewardPool || 0), 0),
          currentStatus: latestEpoch.status,
          participants: latestEpoch.totalParticipants || 0
        });
      }
      
      const verifyRes = await axios.get(`${BACKEND_URL}/api/admin/verification/queue`, { headers: authHeaders });
      // Sort by submittedAt (newest first)
      const sorted = (verifyRes.data.pending || []).sort((a, b) => {
        const dateA = new Date(a.submittedAt || 0);
        const dateB = new Date(b.submittedAt || 0);
        return dateB - dateA;
      });
      setQueue(sorted);
      
      try {
        const priceRes = await axios.get(`${BACKEND_URL}/api/tip-price`);
        if (priceRes.data?.price) {
          setCurrentPrice(priceRes.data.price);
          setTipPrice(priceRes.data.price.toString());
        }
      } catch (e) {
        console.log('No price set');
      }
      
      // Load scoring settings and info
      try {
        const [settingsRes, infoRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/scoring-settings`, { headers: authHeaders }),
          axios.get(`${BACKEND_URL}/api/scoring-info`)
        ]);
        setScoringSettings(settingsRes.data);
        setScoringInfo(infoRes.data);
      } catch (e) {
        console.log('Error loading scoring info:', e);
      }
      
      // Load old contracts
      try {
        const oldRes = await axios.get(`${BACKEND_URL}/api/admin/old-contracts`, { headers: authHeaders });
        setOldContracts(oldRes.data.contracts || []);
      } catch (e) {
        console.log('Error loading old contracts:', e);
      }
      
      // Load QR data
      await loadQrData();
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== QR ADMIN FUNCTIONS ====================
  
  const loadQrData = async () => {
    try {
      setQrLoading(true);
      const authHeaders = getAuthHeaders();
      
      // Load QR list
      const listRes = await axios.get(`${BACKEND_URL}/api/admin/qr`, { headers: authHeaders });
      setQrList(listRes.data.qrCodes || []);
      
      // Calculate stats
      const qrs = listRes.data.qrCodes || [];
      const activeQrs = qrs.filter(q => q.status === 'active').length;
      const inactiveQrs = qrs.filter(q => q.status === 'inactive').length;
      const revokedQrs = qrs.filter(q => q.status === 'revoked').length;
      
      setQrStats({
        total: qrs.length,
        active: activeQrs,
        inactive: inactiveQrs,
        revoked: revokedQrs
      });
      
      // Load QR config
      const configRes = await axios.get(`${BACKEND_URL}/api/admin/qr-config`, { headers: authHeaders });
      setQrConfig(configRes.data);
      
    } catch (e) {
      console.log('Error loading QR data:', e);
    } finally {
      setQrLoading(false);
    }
  };
  
  const handleRevokeQr = async (qrId) => {
    if (!confirm(`Are you sure you want to revoke QR ${qrId}? This cannot be undone.`)) return;
    
    try {
      setRevokingQr(qrId);
      const authHeaders = getAuthHeaders();
      await axios.patch(`${BACKEND_URL}/api/admin/qr/${qrId}/status`, { status: 'revoked' }, { headers: authHeaders });
      toast.success('QR revoked successfully');
      await loadQrData();
    } catch (e) {
      toast.error('Failed to revoke QR');
      console.error(e);
    } finally {
      setRevokingQr(null);
    }
  };
  
  const handleToggleQrStatus = async (qrId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const authHeaders = getAuthHeaders();
      await axios.patch(`${BACKEND_URL}/api/admin/qr/${qrId}/status`, { status: newStatus }, { headers: authHeaders });
      toast.success(`QR ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      await loadQrData();
    } catch (e) {
      toast.error('Failed to update QR status');
      console.error(e);
    }
  };
  
  const handleSaveQrConfig = async () => {
    try {
      setSavingQrConfig(true);
      const authHeaders = getAuthHeaders();
      await axios.patch(`${BACKEND_URL}/api/admin/qr-config`, qrConfig, { headers: authHeaders });
      toast.success('QR config saved');
    } catch (e) {
      toast.error('Failed to save QR config');
      console.error(e);
    } finally {
      setSavingQrConfig(false);
    }
  };
  
  const handleQrPreview = async (epochId) => {
    try {
      setQrPreview({ loading: true, epochId });
      const authHeaders = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/qr-preview/${epochId}`, {}, { headers: authHeaders });
      setQrPreview({ ...res.data, loading: false, epochId });
    } catch (e) {
      toast.error('Failed to load QR preview');
      setQrPreview(null);
    }
  };
  
  const handleDebugWallet = async () => {
    if (!debugWallet) return;
    try {
      const authHeaders = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/qr-wallet/${debugWallet}`, { headers: authHeaders });
      setQrWalletDebug(res.data);
    } catch (e) {
      toast.error('Failed to debug wallet');
      setQrWalletDebug(null);
    }
  };
  
  // ==================== END QR ADMIN FUNCTIONS ====================

  // ==================== V2 EPOCH DEBUG FUNCTIONS ====================
  
  const fetchEpochDebugInfo = async (epochIds) => {
    const debugData = {};
    const authHeaders = getAuthHeaders();
    await Promise.all(
      epochIds.map(async (epochId) => {
        try {
          const res = await axios.get(
            `${BACKEND_URL}/api/admin/epoch-debug/${epochId}`,
            { headers: authHeaders }
          );
          debugData[epochId] = res.data;
        } catch (e) {
          console.log(`Error fetching debug for epoch ${epochId}:`, e);
          debugData[epochId] = null;
        }
      })
    );
    setEpochDebugInfo(prev => ({ ...prev, ...debugData }));
    
    // Also update V2 cutoff from any response
    const firstDebug = Object.values(debugData).find(d => d?.flow_analysis?.v2_cutoff_epoch);
    if (firstDebug?.flow_analysis?.v2_cutoff_epoch) {
      setV2CutoffEpoch(firstDebug.flow_analysis.v2_cutoff_epoch);
    }
  };
  
  // Fetch debug info when epochs change (any change, not just count)
  useEffect(() => {
    if (isAuthenticated && epochs.length > 0) {
      const epochIds = epochs.map(e => e.epochId);
      fetchEpochDebugInfo(epochIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, JSON.stringify(epochs.map(e => ({ id: e.epochId, status: e.status, dist: e.distributorAddress })))]);
  
  // Helper to get flow path badge - USES EPOCH-DEBUG AS SINGLE SOURCE OF TRUTH
  const getFlowPathBadge = (epochId) => {
    const debug = epochDebugInfo[epochId];
    
    // If debug not loaded yet, show loading or infer from epoch status
    if (!debug) {
      // Don't show "Not Computed" by default - wait for actual data
      return <Badge className="bg-blue-400 text-white text-xs ml-2 animate-pulse">...</Badge>;
    }
    
    const flowPath = debug.flow_analysis?.path_used;
    const isV2Required = debug.flow_analysis?.is_v2_required;
    
    if (flowPath === 'V2_CORRECT') {
      return <Badge className="bg-green-500 text-white text-xs ml-2">V2</Badge>;
    } else if (flowPath === 'V1_LEGACY') {
      return (
        <Badge className={`${isV2Required ? 'bg-red-500' : 'bg-yellow-500'} text-white text-xs ml-2`}>
          V1{isV2Required ? ' ⛔' : ''}
        </Badge>
      );
    } else if (flowPath === 'NOT_COMPUTED') {
      return <Badge className="bg-gray-400 text-white text-xs ml-2">Not Computed</Badge>;
    } else if (flowPath === 'MIXED') {
      return <Badge className="bg-orange-500 text-white text-xs ml-2">Mixed</Badge>;
    }
    // Unknown flow path
    return <Badge className="bg-gray-300 text-gray-600 text-xs ml-2">?</Badge>;
  };
  
  // Check if epoch can be deployed - USES EPOCH-DEBUG AS SINGLE SOURCE OF TRUTH
  const canDeployEpoch = (epoch) => {
    const debug = epochDebugInfo[epoch.epochId];
    // If debug not loaded yet, check basic conditions but don't block
    if (!debug) {
      // Optimistic: if epoch has merkleRoot and no distributorAddress, likely can deploy
      // This avoids showing "blocked" while loading
      return epoch.merkleRoot && !epoch.distributorAddress && epoch.status === 'FINALIZED';
    }
    // AUTHORITATIVE: use backend deploy_readiness
    return debug.deploy_readiness?.can_deploy === true;
  };
  
  const getDeployBlockedReason = (epoch) => {
    const debug = epochDebugInfo[epoch.epochId];
    if (!debug) return null; // Still loading - don't show blocked message
    if (debug.deploy_readiness?.can_deploy === true) return null;
    return debug.deploy_readiness?.reason || 'Cannot deploy';
  };
  
  // Check if debug info is still loading for this epoch
  const isDebugLoading = (epoch) => {
    return !epochDebugInfo[epoch.epochId];
  };
  
  // ==================== END V2 EPOCH DEBUG FUNCTIONS ====================

  // Deployable epochs: finalized with merkle root but no distributor yet
  const deployableEpochs = epochs.filter(e => e.merkleRoot && !e.distributorAddress);
  const selectedDeployEpoch = deployableEpochs.find(e => String(e.epochId) === selectedDeployEpochId);

  const handleVerify = async (wallet, platform, approved, item, notes = '') => {
    try {
      setProcessing(wallet);
      const adminWallet = ADMIN_TREASURY;
      
      if (platform === 'youtube') {
        // Get handle from item for YouTube
        const handle = item.handle || item.metadata?.submittedIdentifier || item.socialHandle?.replace('@', '');
        
        // Use foundWhere if available from check result, otherwise default
        const verificationMethod = item.foundWhere === 'ABOUT' 
          ? 'code_in_channel_about' 
          : 'code_in_video_description';
        
        const authHeaders = getAuthHeaders();
        
        if (approved) {
          // Approve YouTube
          await axios.post(`${BACKEND_URL}/api/admin/verification/youtube/approve`, {
            wallet,
            handle,
            verification_method: verificationMethod,
            notes: notes || `Approved by admin (found: ${item.foundWhere || 'manual check'})`
          }, { headers: authHeaders });
        } else {
          // Reject YouTube
          await axios.post(`${BACKEND_URL}/api/admin/verification/youtube/reject`, {
            wallet,
            handle,
            reason: notes || 'Rejected by admin'
          }, { headers: authHeaders });
        }
      } else if (platform === 'tiktok') {
        // TikTok verification
        const authHeaders = getAuthHeaders();
        
        if (approved) {
          // Approve TikTok
          await axios.post(`${BACKEND_URL}/api/admin/verification/tiktok/approve`, {
            wallet,
            notes: notes || 'Approved by admin'
          }, { headers: authHeaders });
        } else {
          // Reject TikTok
          await axios.post(`${BACKEND_URL}/api/admin/verification/tiktok/reject`, {
            wallet,
            reason: notes || 'Rejected by admin'
          }, { headers: authHeaders });
        }
      } else {
        // Use generic endpoint for X/Twitter
        const authHeaders = getAuthHeaders();
        await axios.post(
          `${BACKEND_URL}/api/admin/verification/verify`,
          { wallet, approved, notes },
          { headers: authHeaders }
        );
      }
      
      // Optimistic UI update - remove item from queue immediately
      setQueue(prev => prev.filter(q => q.wallet !== wallet || q.platform !== platform));
      setFilteredQueue(prev => prev.filter(q => q.wallet !== wallet || q.platform !== platform));
      
      toast.success(`${platform === 'twitter' ? 'X' : platform === 'youtube' ? 'YouTube' : 'TikTok'} verification ${approved ? 'approved' : 'rejected'}!`);
      
      // Also refresh from server to ensure consistency
      loadAllData();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process verification');
      // Reload queue on error to restore correct state
      loadAllData();
    } finally {
      setProcessing(null);
    }
  };

  // YouTube Check function - automated verification code search
  const handleYouTubeCheck = async (item) => {
    try {
      setChecking(item.wallet);
      const adminWallet = ADMIN_TREASURY;
      const channelId = item.channelId || item.metadata?.channelId;
      
      if (!channelId) {
        toast.error('Channel ID not found');
        return;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/verification/youtube/check`,
        {
          wallet: item.wallet,
          channelId: channelId,
          verificationCode: item.verificationCode
        }
      );
      
      const result = response.data;
      
      if (result.checkStatus === 'FOUND') {
        toast.success(`Code FOUND in ${result.foundWhere === 'ABOUT' ? 'Channel About' : 'Video Description'}!`);
      } else if (result.checkStatus === 'NOT_FOUND') {
        toast.info('Code not found on channel');
      } else if (result.checkStatus === 'ERROR') {
        toast.error(`Check failed: ${result.checkError || 'Unknown error'}`);
      }
      
      // Refresh the queue to show updated check status
      loadAllData();
      
    } catch (error) {
      console.error('Check error:', error);
      toast.error(error.response?.data?.detail || 'Check failed');
    } finally {
      setChecking(null);
    }
  };

  // Get check status badge for YouTube items
  const getYouTubeCheckStatusBadge = (item) => {
    const status = item.checkStatus;
    
    if (!status || status === 'NOT_CHECKED') {
      return (
        <Badge variant="outline" className="text-xs bg-slate-100 text-slate-500 border-slate-200">
          <HelpCircle className="w-3 h-3 mr-1" />
          Not Checked
        </Badge>
      );
    }
    
    if (status === 'FOUND') {
      return (
        <Badge className="text-xs bg-green-100 text-green-700 border-green-300">
          <CheckCheck className="w-3 h-3 mr-1" />
          FOUND
        </Badge>
      );
    }
    
    if (status === 'NOT_FOUND') {
      return (
        <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-300">
          <AlertTriangle className="w-3 h-3 mr-1" />
          NOT FOUND
        </Badge>
      );
    }
    
    if (status === 'ERROR') {
      return (
        <Badge className="text-xs bg-red-100 text-red-700 border-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          ERROR
        </Badge>
      );
    }
    
    return null;
  };
  
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube':
        return <Youtube className="w-4 h-4" />;
      case 'tiktok':
        return <Video className="w-4 h-4" />;
      case 'twitter':
      default:
        return <Twitter className="w-4 h-4" />;
    }
  };
  
  const getPlatformBadgeColor = (platform) => {
    switch (platform) {
      case 'youtube':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'tiktok':
        return 'bg-pink-500/10 text-pink-500 border-pink-500/20';
      case 'twitter':
      default:
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    }
  };
  
  const getVerificationUrl = (item) => {
    if (item.platform === 'youtube') {
      // Try to build YouTube URL - prefer handle for user-friendly link
      const handle = item.handle || item.metadata?.submittedIdentifier;
      if (handle) {
        return `https://www.youtube.com/@${handle.replace('@', '')}`;
      }
      // Fallback to channel ID
      const channelId = item.channelId || item.metadata?.channelId;
      if (channelId) {
        return `https://www.youtube.com/channel/${channelId}/about`;
      }
      return null;
    } else if (item.platform === 'tiktok') {
      // For TikTok, prefer the video URL (direct link to proof)
      const videoUrl = item.videoUrl || item.metadata?.tiktokVideoUrl;
      if (videoUrl) {
        return videoUrl;
      }
      // Fallback to profile URL
      const handle = item.handle || item.metadata?.tiktokUsername;
      if (handle) {
        return `https://www.tiktok.com/@${handle.replace('@', '')}`;
      }
      return null;
    } else {
      // X/Twitter
      const handle = item.socialHandle || item.handle;
      if (handle) {
        return `https://x.com/${handle.replace('@', '')}`;
      }
      return null;
    }
  };
  
  const getDisplayIdentifier = (item) => {
    if (item.platform === 'youtube') {
      // Show the user-friendly identifier
      return item.handle || item.metadata?.submittedIdentifier || item.socialHandle?.replace('@', '') || 'Unknown';
    }
    if (item.platform === 'tiktok') {
      return item.handle || item.metadata?.tiktokUsername || 'Unknown';
    }
    return item.socialHandle || item.handle || 'Unknown';
  };
  
  const getCanonicalIdentifier = (item) => {
    if (item.platform === 'youtube') {
      // channelId is now also flattened at the top level
      return item.channelId || item.metadata?.channelId || null;
    }
    if (item.platform === 'tiktok') {
      // TikTok username is the canonical identifier
      return item.handle || item.metadata?.tiktokUsername || null;
    }
    return null;
  };
  
  // Count items per platform
  const platformCounts = {
    all: queue.length,
    twitter: queue.filter(i => i.platform === 'twitter').length,
    youtube: queue.filter(i => i.platform === 'youtube').length,
    tiktok: queue.filter(i => i.platform === 'tiktok').length,
  };

  const handleSavePrice = async () => {
    if (!tipPrice || isNaN(parseFloat(tipPrice))) {
      toast.error('Please enter a valid price');
      return;
    }
    
    try {
      setSavingPrice(true);
      const authHeaders = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/set-tip-price?price=${parseFloat(tipPrice)}`, {}, { headers: authHeaders });
      setCurrentPrice(parseFloat(tipPrice));
      toast.success('TIP price updated!');
    } catch (error) {
      console.error('Error saving price:', error);
      toast.error('Failed to save price');
    } finally {
      setSavingPrice(false);
    }
  };

  // Load admin settings
  const loadAdminSettings = async () => {
    try {
      const authHeaders = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/settings`, { headers: authHeaders });
      setAdminSettings(res.data);
    } catch (error) {
      console.error('Error loading admin settings:', error);
    }
  };

  // Setup admin wallet (first time)
  const handleSetupWallet = async () => {
    if (!isConnected || !walletClient) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setSettingUpWallet(true);
      
      // Create message to sign
      const timestamp = Date.now();
      const message = `TipMe Admin Setup\n\nWallet: ${address}\nTimestamp: ${timestamp}\n\nI confirm I want to set this wallet as the admin wallet.`;
      
      // Request signature from wallet
      const signature = await walletClient.signMessage({
        message: message,
      });
      
      // Send to backend
      await axios.post(`${BACKEND_URL}/api/admin/setup-wallet`, {
        wallet: address,
        signature: signature,
        message: message,
        newPassword: newPassword,
      });
      
      toast.success('Admin wallet configured! Your new password is now active.');
      setNewPassword('');
      setConfirmPassword('');
      loadAdminSettings();
    } catch (error) {
      console.error('Setup wallet error:', error);
      toast.error(error.response?.data?.detail || 'Failed to setup wallet');
    } finally {
      setSettingUpWallet(false);
    }
  };

  // Change password (requires wallet signature)
  const handleChangePassword = async () => {
    if (!isConnected || !walletClient) {
      toast.error('Please connect your wallet first');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setChangingPassword(true);
      
      // Create message to sign
      const timestamp = Date.now();
      const message = `TipMe Password Change\n\nWallet: ${address}\nTimestamp: ${timestamp}\n\nI confirm I want to change the admin password.`;
      
      // Request signature from wallet
      const signature = await walletClient.signMessage({
        message: message,
      });
      
      // Send to backend
      await axios.post(`${BACKEND_URL}/api/admin/change-password`, {
        wallet: address,
        signature: signature,
        message: message,
        newPassword: newPassword,
      });
      
      toast.success('Password changed successfully!');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Change password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveScoringSettings = async () => {
    try {
      setSavingSettings(true);
      const authHeaders = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/scoring-settings`, null, {
        params: {
          maxPointsPerWallet: scoringSettings.maxPointsPerWallet,
          tipperPoolRatio: scoringSettings.tipperPoolRatio,
          creatorPoolRatio: scoringSettings.creatorPoolRatio,
          sybilPenalty: scoringSettings.sybilPenalty
        },
        headers: authHeaders
      });
      toast.success('Scoring settings saved!');
      loadAllData();
    } catch (error) {
      console.error('Error saving scoring settings:', error);
      toast.error('Failed to save scoring settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // ==================== SYBIL WHITELIST FUNCTIONS ====================
  
  // Validate whitelist input and update counts
  // ==================== SYBIL WHITELIST FUNCTIONS DEPRECATED (2026-03-01) ====================
  // Sybil V1 has been removed from the scoring pipeline. These functions are no longer used.
  // Whitelist validation and save functions removed.
  // ==================== END SYBIL WHITELIST FUNCTIONS ====================

  const handleFinalizeEpoch = async () => {
    try {
      setFinalizing(true);
      const authHeaders = getAuthHeaders();
      
      // Get the latest COLLECTING epoch
      const collectingEpoch = epochs.find(e => e.status === 'COLLECTING') || epochs[0];
      if (!collectingEpoch) {
        toast.error('No epoch to finalize');
        return;
      }
      
      // V2 TWO-PHASE FINALIZATION: compute → lock
      // Step 1: Compute scores (writes to epochFrozenScores)
      toast.info('Step 1/2: Computing scores...');
      const computeRes = await axios.post(`${BACKEND_URL}/api/admin/epoch/${collectingEpoch.epochId}/compute`, {}, { headers: authHeaders });
      
      if (!computeRes.data.success) {
        throw new Error(computeRes.data.detail || 'Compute failed');
      }
      
      toast.info(`Computed ${computeRes.data.participantCount} participants. Step 2/2: Locking...`);
      
      // Step 2: Lock epoch (generates Merkle from epochFrozenScores)
      const lockRes = await axios.post(`${BACKEND_URL}/api/admin/epoch/${collectingEpoch.epochId}/lock`, {}, { headers: authHeaders });
      
      if (!lockRes.data.success) {
        throw new Error(lockRes.data.detail || 'Lock failed');
      }
      
      // Create next epoch for continuous tip collection
      try {
        await axios.post(`${BACKEND_URL}/api/admin/create-epoch`, {}, { headers: authHeaders });
      } catch (e) {
        console.log('Next epoch may already exist:', e);
      }
      
      toast.success(`Epoch ${collectingEpoch.epochId} finalized (V2)! Merkle: ${lockRes.data.merkleRoot?.slice(0, 12)}...`);
      loadAllData();
    } catch (error) {
      console.error('Finalize error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to finalize epoch');
    } finally {
      setFinalizing(false);
    }
  };

  // Two-phase finalization handlers
  const handleComputeScores = async (epochId) => {
    try {
      setComputing(true);
      const authHeaders = getAuthHeaders();
      toast.info('Computing scores (includes Q multiplier)...');
      const res = await axios.post(`${BACKEND_URL}/api/admin/epoch/${epochId}/compute`, {}, { headers: authHeaders });
      toast.success(`Scores computed! ${res.data.participantCount} participants. Ready to lock.`);
      loadAllData();
      // Refresh finalization status and debug info
      loadFinalizationStatus(epochId);
      fetchEpochDebugInfo([epochId]);
    } catch (error) {
      console.error('Compute error:', error);
      toast.error(error.response?.data?.detail || 'Failed to compute scores');
    } finally {
      setComputing(false);
    }
  };

  const handleLockEpoch = async (epochId) => {
    try {
      setLocking(true);
      const authHeaders = getAuthHeaders();
      toast.info('Generating Merkle tree and locking...');
      const res = await axios.post(`${BACKEND_URL}/api/admin/epoch/${epochId}/lock`, {}, { headers: authHeaders });
      toast.success(`Epoch locked! Merkle root: ${res.data.merkleRoot?.slice(0, 10)}...`);
      loadAllData();
      // Explicitly refresh debug info to update UI state immediately
      fetchEpochDebugInfo([epochId]);
    } catch (error) {
      console.error('Lock error:', error);
      toast.error(error.response?.data?.detail || 'Failed to lock epoch');
    } finally {
      setLocking(false);
    }
  };

  // Data Integrity Panel Handler (replaces legacy Sybil Preview)
  const loadDataIntegrity = async (epochId) => {
    setDataIntegrityLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.get(`${BACKEND_URL}/api/admin/epoch-integrity/${epochId}`, { headers: authHeaders });
      setDataIntegrity(response.data);
    } catch (error) {
      console.error('Data integrity load error:', error);
      toast.error(error.response?.data?.detail || 'Failed to load data integrity status');
      setDataIntegrity(null);
    } finally {
      setDataIntegrityLoading(false);
    }
  };

  // Helper to get status badge color for data integrity
  const getIntegrityStatusBadge = (status) => {
    switch (status) {
      case 'OK':
        return <Badge className="bg-green-100 text-green-700 border-green-300">OK</Badge>;
      case 'DEGRADED':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-300">DEGRADED</Badge>;
      case 'UNKNOWN':
        return <Badge className="bg-gray-100 text-gray-500 border-gray-300">UNKNOWN</Badge>;
      case 'NOT_COMPUTED':
        return <Badge className="bg-blue-100 text-blue-600 border-blue-300">NOT COMPUTED</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-500 border-gray-300">{status}</Badge>;
    }
  };

  const loadFinalizationStatus = async (epochId) => {
    try {
      const authHeaders = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/epoch/${epochId}/finalization-status`, { headers: authHeaders });
      setFinalizationStatus(prev => ({ ...prev, [epochId]: res.data }));
    } catch (error) {
      console.error('Failed to load finalization status:', error);
    }
  };

  const handleCreateEpoch = async () => {
    try {
      setCreatingEpoch(true);
      const authHeaders = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/create-epoch`, {}, { headers: authHeaders });
      toast.success(`Epoch ${res.data.epochId} created! Now collecting tips.`);
      loadAllData();
    } catch (error) {
      console.error('Create epoch error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create epoch');
    } finally {
      setCreatingEpoch(false);
    }
  };

  const handleDeployDistributor = async (epoch) => {
    if (!walletClient || !isTreasuryWallet) {
      toast.error('Please connect with treasury wallet');
      return;
    }

    if (!epoch.merkleRoot) {
      toast.error('Epoch not finalized - no Merkle root');
      return;
    }

    try {
      setDeploying(true);
      toast.info('Deploying Distributor contract...');

      // Calculate claim deadline (5 days from now)
      const claimDeadline = BigInt(Math.floor(Date.now() / 1000) + (5 * 24 * 60 * 60));
      
      // Encode constructor arguments
      const constructorArgs = encodeAbiParameters(
        parseAbiParameters('bytes32, uint256, address, address, uint256'),
        [
          epoch.merkleRoot,
          claimDeadline,
          TREASURY_ADDRESS,
          TIP_TOKEN_ADDRESS,
          BigInt(epoch.epochId)
        ]
      );

      // Full deployment bytecode with constructor args
      const deploymentData = DISTRIBUTOR_BYTECODE + constructorArgs.slice(2);

      // For Trezor compatibility, use sendTransaction with explicit account
      // Contract deployment = transaction with no 'to' address
      const hash = await walletClient.sendTransaction({
        account: address,
        data: deploymentData,
        chain: walletClient.chain,
      });

      toast.info('Waiting for confirmation...');
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      const distributorAddress = receipt.contractAddress;

      if (!distributorAddress) {
        throw new Error('Contract deployment failed - no address returned');
      }

      // Update backend with distributor address
      const authHeaders = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/set-distributor`, {
        epochId: epoch.epochId,
        distributorAddress
      }, { headers: authHeaders });

      toast.success(`Distributor deployed at ${distributorAddress.slice(0, 10)}...`);
      loadAllData();
    } catch (error) {
      console.error('Deploy error:', error);
      toast.error('Failed to deploy: ' + (error.shortMessage || error.message || 'Unknown error'));
    } finally {
      setDeploying(false);
    }
  };

  const handleSetManualDistributor = async (epochId) => {
    if (!manualDistributorAddress || !manualDistributorAddress.startsWith('0x') || manualDistributorAddress.length !== 42) {
      toast.error('Please enter a valid contract address (0x...)');
      return;
    }

    try {
      setSettingDistributor(true);
      const authHeaders = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/admin/set-distributor`, {
        epochId: epochId,
        distributorAddress: manualDistributorAddress
      }, { headers: authHeaders });
      toast.success('Distributor address set successfully!');
      setManualDistributorAddress('');
      loadAllData();
      // Explicitly refresh epoch debug info to update deploy_readiness
      fetchEpochDebugInfo([epochId]);
    } catch (error) {
      console.error('Error setting distributor:', error);
      toast.error('Failed to set distributor address');
    } finally {
      setSettingDistributor(false);
    }
  };

  const handleServerDeploy = async (epochId) => {
    try {
      setServerDeploying(true);
      setDeployerInfo(null);
      const authHeaders = getAuthHeaders();
      
      const res = await axios.post(`${BACKEND_URL}/api/admin/deploy-distributor/${epochId}`, {}, { headers: authHeaders });
      
      if (res.data.needsFunding) {
        // Deployer needs BNB for gas
        setDeployerInfo({
          address: res.data.deployerAddress,
          currentBalance: res.data.currentBalance,
          required: res.data.requiredBalance
        });
        toast.info(`Send 0.005 BNB to deployer wallet, then try again`);
      } else if (res.data.success) {
        toast.success(`Contract deployed at ${res.data.contractAddress.slice(0, 10)}...`);
        setDeployerInfo(null);
        loadAllData();
        // Explicitly refresh epoch debug info to update deploy_readiness
        fetchEpochDebugInfo([epochId]);
      } else {
        toast.error(res.data.message || res.data.detail || 'Deployment failed');
      }
    } catch (error) {
      console.error('Server deploy error:', error);
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || 'Deployment failed';
      toast.error(errorDetail);
    } finally {
      setServerDeploying(false);
    }
  };

  const handleReturnUnclaimed = async (contractAddress) => {
    try {
      setReturningFunds(contractAddress);
      const authHeaders = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/admin/return-unclaimed/${contractAddress}`, {}, { headers: authHeaders });
      
      if (res.data.success) {
        toast.success(`Returned ${res.data.amountReturned.toFixed(6)} BNB to treasury!`);
        loadAllData();
      } else if (res.data.needsGas) {
        toast.info(`Deployer needs gas. Send 0.002 BNB to ${res.data.deployerAddress}`);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.error('Return unclaimed error:', error);
      toast.error(error.response?.data?.detail || 'Failed to return funds');
    } finally {
      setReturningFunds(null);
    }
  };

  const handleFundDistributor = async (epoch) => {
    if (!walletClient || !isTreasuryWallet) {
      toast.error('Please connect with treasury wallet');
      return;
    }

    if (!epoch.distributorAddress) {
      toast.error('No distributor address set for this epoch');
      return;
    }

    const amount = fundAmount || epoch.totalRewardPool;
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Invalid funding amount');
      return;
    }

    try {
      setFunding(true);
      
      // Send BNB to distributor contract
      const hash = await walletClient.sendTransaction({
        account: address,
        to: epoch.distributorAddress,
        value: parseUnits(amount.toString(), 18),
        chain: walletClient.chain,
      });

      toast.info('Waiting for confirmation...');
      
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      
      if (receipt.status === 'success') {
        toast.success(`Funded distributor with ${amount} BNB!`);
        loadAllData();
        fetchTreasuryBalance();
      } else {
        toast.error('Transaction failed');
      }
    } catch (error) {
      console.error('Fund error:', error);
      toast.error('Failed to fund: ' + (error.shortMessage || error.message));
    } finally {
      setFunding(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      'COLLECTING': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      'PROCESSING': 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      'COMPUTED': 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      'FINALIZED': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      'CLAIMABLE': 'bg-green-500/10 text-green-600 border-green-500/20',
      'COMPLETED': 'bg-gray-500/10 text-gray-600 border-gray-500/20'
    };
    return <Badge className={styles[status] || styles['COLLECTING']}>{status}</Badge>;
  };

  // Login Screen - Wallet Signature Based
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#d4cfc4] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-[#e8e3db]/50 border-[#b8b3ab]">
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 mx-auto mb-4 text-[#f0b90b]" />
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-2">Admin Access</h2>
            <p className="text-[#666]">Sign in with your admin wallet</p>
          </div>
          
          {/* Wallet Connection Status */}
          {!isConnected ? (
            <div className="space-y-4">
              <Alert className="border-amber-500/20 bg-amber-500/10">
                <Wallet className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Connect your wallet to access the admin panel
                </AlertDescription>
              </Alert>
              <Button 
                onClick={openConnectModal}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Connect Wallet
              </Button>
            </div>
          ) : !isAdminWallet ? (
            <div className="space-y-4">
              <Alert className="border-red-500/20 bg-red-500/10">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  <strong>Wrong wallet connected!</strong>
                  <br />
                  Please connect with the admin wallet:
                  <code className="block mt-2 text-xs bg-red-100 px-2 py-1 rounded font-mono break-all">
                    {TREASURY_ADDRESS}
                  </code>
                </AlertDescription>
              </Alert>
              <div className="text-center text-sm text-[#888]">
                Connected: {address?.slice(0, 6)}...{address?.slice(-4)}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert className="border-green-500/20 bg-green-500/10">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Admin wallet connected. Click below to sign in.
                </AlertDescription>
              </Alert>
              
              {authError && (
                <Alert className="border-red-500/20 bg-red-500/10">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">
                    {authError}
                  </AlertDescription>
                </Alert>
              )}
              
              <Button 
                onClick={async () => {
                  const success = await adminLogin();
                  if (success) {
                    toast.success('Admin access granted');
                    loadAllData();
                  }
                }}
                disabled={authLoading}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold"
                data-testid="admin-sign-in-btn"
              >
                {authLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing...</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" /> Sign In with Wallet</>
                )}
              </Button>
              
              <p className="text-xs text-center text-[#888]">
                You will be asked to sign a message to verify ownership.
                <br />
                This does not cost any gas.
              </p>
            </div>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#d4cfc4] text-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#e8e3db] border-b border-[#b8b3ab] px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <Shield className="w-8 h-8 text-[#f0b90b]" />
            <h1 className="text-xl font-bold">TipMe Admin</h1>
            {getTimeRemaining() && (
              <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                <Clock className="w-3 h-3 mr-1" />
                Session: {getTimeRemaining()}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={loadAllData} variant="outline" size="sm" className="border-[#b8b3ab]">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {isConnected ? (
              <div className="flex items-center gap-2">
                {isTreasuryWallet ? (
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Treasury Connected
                  </Badge>
                ) : (
                  <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Wrong Wallet
                  </Badge>
                )}
                <span className="text-sm font-mono text-[#666]">
                  {address?.slice(0, 6)}...{address?.slice(-4)}
                </span>
              </div>
            ) : (
              <Button 
                onClick={openConnectModal}
                className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
              >
                <Wallet className="w-4 h-4 mr-2" />
                Connect Wallet
              </Button>
            )}
            <Button 
              onClick={async () => {
                await adminLogout();
                toast.success('Logged out');
              }}
              variant="outline" 
              size="sm" 
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Treasury Warning */}
      {isConnected && !isTreasuryWallet && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">
              <strong>Wrong wallet connected!</strong> To deploy contracts and fund distributors, connect with the treasury wallet: 
              <code className="ml-2 bg-red-100 px-2 py-0.5 rounded font-mono text-xs">{TREASURY_ADDRESS}</code>
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-[#e8e3db] border-b border-[#b8b3ab]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 items-center">
            <button
              onClick={() => setActiveTab('epochs')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'epochs' 
                  ? 'text-[#f0b90b] border-b-2 border-[#f0b90b]' 
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Epoch Management
            </button>
            <button
              onClick={() => setActiveTab('verification')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'verification' 
                  ? 'text-[#f0b90b] border-b-2 border-[#f0b90b]' 
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Verification Queue
              {queue.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{queue.length}</Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'settings' 
                  ? 'text-[#f0b90b] border-b-2 border-[#f0b90b]' 
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              <Banknote className="w-4 h-4 inline mr-2" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab('qr')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'qr' 
                  ? 'text-[#f0b90b] border-b-2 border-[#f0b90b]' 
                  : 'text-[#666] hover:text-[#1a1a1a]'
              }`}
            >
              <QrCode className="w-4 h-4 inline mr-2" />
              QR Codes
              {qrStats?.active > 0 && (
                <Badge className="ml-2 bg-emerald-500 text-white">{qrStats.active}</Badge>
              )}
            </button>
            <div className="ml-auto">
              <button
                data-testid="admin-manual-refresh-btn"
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="px-3 py-2 text-sm text-[#666] hover:text-[#1a1a1a] transition-colors flex items-center gap-1.5"
                title="Refresh data"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#f0b90b]" />
          </div>
        ) : activeTab === 'epochs' ? (
          /* EPOCHS TAB */
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="p-5 bg-gradient-to-br from-[#f0b90b] to-[#d4a50a] border-0 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Banknote className="w-5 h-5 opacity-80" />
                  <span className="text-sm opacity-80">Treasury Balance</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(parseFloat(treasuryBalance))} BNB</p>
              </Card>
              
              <Card className="p-5 bg-white border-[#b8b3ab]">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#f0b90b]" />
                  <span className="text-sm text-[#666]">Total Epochs</span>
                </div>
                <p className="text-2xl font-bold text-[#1a1a1a]">{stats?.totalEpochs || 0}</p>
              </Card>
              
              <Card className="p-5 bg-white border-[#b8b3ab]">
                <div className="flex items-center gap-3 mb-2">
                  <Gift className="w-5 h-5 text-[#f0b90b]" />
                  <span className="text-sm text-[#666]">Total Distributed</span>
                </div>
                <p className="text-2xl font-bold text-[#1a1a1a]">{formatNumber(stats?.totalDistributed || 0)} BNB</p>
              </Card>
              
              <Card className="p-5 bg-white border-[#b8b3ab]">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#f0b90b]" />
                  <span className="text-sm text-[#666]">Pending Verifications</span>
                </div>
                <p className="text-2xl font-bold text-[#1a1a1a]">{queue.length}</p>
              </Card>
            </div>

            {/* Scheduler Status */}
            {schedulerStatus && (
              <Card className="p-4 bg-indigo-50 border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className="font-medium text-indigo-800">
                      Auto-Rotation: {schedulerStatus.scheduler_running ? 'Active' : 'Stopped'}
                    </span>
                  </div>
                  <span className="text-sm text-indigo-600">
                    Next check: {schedulerStatus.jobs?.[0]?.next_run ? 
                      new Date(schedulerStatus.jobs[0].next_run).toLocaleTimeString() : 'N/A'}
                  </span>
                </div>
                <p className="text-xs text-indigo-500 mt-2">
                  Epochs auto-finalize and new ones start automatically when the week ends
                </p>
              </Card>
            )}

            {/* Action Alerts - Compact Summary */}
            {alerts.length > 0 && (
              <div className="space-y-3">
                {/* Group READY_TO_DEPLOY alerts into a single compact card */}
                {(() => {
                  const deployAlerts = alerts.filter(a => a.type === 'READY_TO_DEPLOY');
                  const otherAlerts = alerts.filter(a => a.type !== 'READY_TO_DEPLOY');
                  
                  return (
                    <>
                      {/* Deploy alerts - collapsed into one line */}
                      {deployAlerts.length > 0 && (
                        <Alert className="border-2 bg-blue-50 border-blue-400">
                          <div className="flex items-center gap-3">
                            <Rocket className="w-5 h-5 text-blue-600" />
                            <AlertDescription className="flex-1 font-medium">
                              {deployAlerts.length} epoch{deployAlerts.length > 1 ? 's' : ''} ready to deploy distributor
                            </AlertDescription>
                            <Badge className="bg-blue-200 text-blue-700">
                              {deployAlerts.map(a => a.message.match(/Epoch (\d+)/)?.[1]).filter(Boolean).join(', ')}
                            </Badge>
                          </div>
                        </Alert>
                      )}
                      
                      {/* Other alerts shown normally */}
                      {otherAlerts.map((alert, idx) => (
                        <Alert 
                          key={idx} 
                          className={`border-2 ${
                            alert.type === 'READY_TO_FINALIZE' ? 'bg-amber-50 border-amber-400' :
                            alert.type === 'CLAIM_ENDING_SOON' ? 'bg-orange-50 border-orange-400' :
                            alert.type === 'CLAIM_ENDED' ? 'bg-red-50 border-red-400' :
                            'bg-gray-50 border-gray-400'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {alert.type === 'READY_TO_FINALIZE' && <Clock className="w-5 h-5 text-amber-600" />}
                            {alert.type === 'CLAIM_ENDING_SOON' && <AlertTriangle className="w-5 h-5 text-orange-600" />}
                            {alert.type === 'CLAIM_ENDED' && <RotateCcw className="w-5 h-5 text-red-600" />}
                            <AlertDescription className="flex-1 font-medium">
                              {alert.message}
                            </AlertDescription>
                            {alert.type === 'READY_TO_FINALIZE' && (
                              <Button
                                size="sm"
                                onClick={handleFinalizeEpoch}
                                disabled={finalizing}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                              >
                                {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finalize Now'}
                              </Button>
                            )}
                          </div>
                        </Alert>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* Active Epoch Indicator — always shown when active epoch exists */}
            {activeEpoch && (
              <Card className="p-4 bg-green-50 border-2 border-green-400">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-medium text-green-800">
                    Epoch {activeEpoch.epochId} is actively collecting tips
                  </span>
                  <span className="text-sm text-green-600 ml-auto">
                    Ends: {new Date(activeEpoch.endTime).toLocaleDateString()}
                  </span>
                </div>
                {/* Countdown Timer */}
                <CountdownTimer targetDate={activeEpoch.endTime} label="Epoch ends in" />
                {/* Two-Phase Finalization Controls */}
                <div className="mt-3 pt-3 border-t border-green-300">
                  <div className="text-xs text-green-700 mb-2 font-medium">
                    Two-Phase Finalization:
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => handleComputeScores(activeEpoch.epochId)}
                      disabled={computing || activeEpoch.status === 'COMPUTED'}
                      variant="outline"
                      className="border-blue-500 text-blue-700 hover:bg-blue-100"
                    >
                      {computing ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Computing...</>
                      ) : (
                        <><Play className="w-4 h-4 mr-1" /> 1. Compute Scores</>
                      )}
                    </Button>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <Button
                      size="sm"
                      onClick={() => handleLockEpoch(activeEpoch.epochId)}
                      disabled={locking || activeEpoch.status !== 'COMPUTED'}
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-100"
                    >
                      {locking ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Locking...</>
                      ) : (
                        <><Shield className="w-4 h-4 mr-1" /> 2. Lock & Merkle</>
                      )}
                    </Button>
                  </div>
                  <div className="text-xs text-green-600 mt-2">
                    Q multiplier applied during compute. Lock generates Merkle (irreversible).
                  </div>
                </div>
                
                {/* Data Integrity Panel (replaces legacy Sybil V1 controls) */}
                <div className="mt-3 pt-3 border-t border-green-300">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-green-700 font-medium flex items-center gap-2">
                      <Shield className="w-3 h-3" />
                      Data Integrity Status
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => loadDataIntegrity(activeEpoch.epochId)}
                      disabled={dataIntegrityLoading}
                      className="h-7 px-2 text-xs"
                      data-testid="refresh-integrity-btn"
                    >
                      {dataIntegrityLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                  
                  {dataIntegrity && dataIntegrity.epoch_id === activeEpoch.epochId ? (
                    <div className="space-y-2">
                      {/* Overall Status */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-green-700">Overall:</span>
                        {getIntegrityStatusBadge(dataIntegrity.overall_status)}
                      </div>
                      
                      {/* Status Grid */}
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {/* Transfer Indexer */}
                        <div className="bg-white/50 rounded-lg p-2" title="Spend Ratio depends on this indexer">
                          <div className="font-medium text-green-800 mb-1">Transfer Indexer</div>
                          {getIntegrityStatusBadge(dataIntegrity.health?.transfer_indexer?.status)}
                          {dataIntegrity.health?.transfer_indexer?.behind_by > 0 && (
                            <div className="text-[10px] text-amber-600 mt-1">
                              {dataIntegrity.health.transfer_indexer.behind_by} blocks behind
                            </div>
                          )}
                        </div>
                        
                        {/* Funding Indexer */}
                        <div className="bg-white/50 rounded-lg p-2" title="Common Funder detection depends on this">
                          <div className="font-medium text-green-800 mb-1">Funding Graph</div>
                          {getIntegrityStatusBadge(dataIntegrity.health?.funding_indexer?.status)}
                          {dataIntegrity.health?.funding_indexer?.coverage_pct !== undefined && (
                            <div className="text-[10px] text-gray-600 mt-1">
                              {dataIntegrity.health.funding_indexer.coverage_pct}% coverage
                            </div>
                          )}
                        </div>
                        
                        {/* Scoring */}
                        <div className="bg-white/50 rounded-lg p-2" title="Scoring computation status">
                          <div className="font-medium text-green-800 mb-1">Scoring</div>
                          {getIntegrityStatusBadge(dataIntegrity.health?.scoring?.status)}
                          {dataIntegrity.health?.scoring?.tip_count !== undefined && (
                            <div className="text-[10px] text-gray-600 mt-1">
                              {dataIntegrity.health.scoring.tip_count} tips
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Recommendations */}
                      {dataIntegrity.recommendations?.length > 0 && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="text-xs font-medium text-amber-800 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Recommendations
                          </div>
                          <ul className="text-[10px] text-amber-700 space-y-1">
                            {dataIntegrity.recommendations.map((rec, i) => (
                              <li key={i}>• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-green-600">
                      Click refresh to load pipeline health status. This shows indexer coverage and helps identify fail-open conditions.
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Awaiting Finalization — independently shown for ANY COMPUTED epoch not yet locked */}
            {epochs.filter(e => e.status === 'COMPUTED' && !e.merkleLocked && !e.distributorAddress && (!activeEpoch || e.epochId !== activeEpoch.epochId)).map(computedEpoch => (
              <Card key={computedEpoch.epochId} className="p-4 bg-yellow-50 border-2 border-yellow-400" data-testid={`awaiting-lock-epoch-${computedEpoch.epochId}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Timer className="w-5 h-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">
                    Epoch {computedEpoch.epochId} computed — awaiting lock
                  </span>
                </div>
                {computedEpoch.lockDeadlineAt && (
                  <CountdownTimer 
                    targetDate={computedEpoch.lockDeadlineAt} 
                    label="Auto-lock in" 
                  />
                )}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-yellow-700">
                    Scores frozen with Q. Review and lock when ready.
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleLockEpoch(computedEpoch.epochId)}
                    disabled={locking}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white"
                    data-testid={`lock-epoch-${computedEpoch.epochId}-btn`}
                  >
                    {locking ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Locking...</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-2" /> Lock & Merkle</>
                    )}
                  </Button>
                </div>
              </Card>
            ))}

            {/* No active epoch warning — only when truly nothing is active or computed */}
            {!activeEpoch && !epochs.find(e => e.status === 'COMPUTED') && (
              <Card className="p-4 bg-red-50 border-2 border-red-400">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-red-800">
                      No active epoch! Tips are not being collected.
                    </span>
                  </div>
                  <Button
                    onClick={handleCreateEpoch}
                    disabled={creatingEpoch}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    {creatingEpoch ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                    ) : (
                      <><Play className="w-4 h-4 mr-2" /> Start New Epoch</>
                    )}
                  </Button>
                </div>
              </Card>
            )}

            {/* Epochs Table */}
            <Card className="bg-white border-[#b8b3ab] overflow-hidden">
              <div className="p-4 border-b border-[#b8b3ab]">
                <h2 className="text-lg font-bold text-[#1a1a1a]">Epochs</h2>
              </div>
              
              {/* Compact Distributor Deployment Section */}
              {deployableEpochs.length > 0 && (
                <div className="p-4 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Rocket className="w-4 h-4 text-amber-600" />
                    <span className="font-medium text-amber-800">Deploy Distributor</span>
                    <Badge className="bg-amber-200 text-amber-700 text-xs">{deployableEpochs.length} pending</Badge>
                  </div>
                  
                  <div className="flex flex-wrap items-end gap-3">
                    {/* Epoch Selector */}
                    <div className="flex-1 min-w-[200px]">
                      <Select value={selectedDeployEpochId} onValueChange={setSelectedDeployEpochId}>
                        <SelectTrigger className="bg-white border-amber-300" data-testid="deploy-epoch-selector">
                          <SelectValue placeholder="Select epoch to deploy..." />
                        </SelectTrigger>
                        <SelectContent>
                          {deployableEpochs.map(e => (
                            <SelectItem key={e.epochId} value={String(e.epochId)}>
                              Epoch #{e.epochId} — {formatNumber(e.totalRewardPool || 0)} BNB ({e.totalParticipants || 0} participants)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Deploy Button */}
                    <Button
                      onClick={() => selectedDeployEpoch && handleServerDeploy(selectedDeployEpoch.epochId)}
                      disabled={!selectedDeployEpoch || serverDeploying || (selectedDeployEpoch && !isDebugLoading(selectedDeployEpoch) && !canDeployEpoch(selectedDeployEpoch))}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="deploy-distributor-btn"
                    >
                      {serverDeploying ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deploying...</>
                      ) : (
                        <><Rocket className="w-4 h-4 mr-2" /> Deploy</>
                      )}
                    </Button>
                  </div>
                  
                  {/* Blocked Reason (if applicable) */}
                  {selectedDeployEpoch && !isDebugLoading(selectedDeployEpoch) && !canDeployEpoch(selectedDeployEpoch) && getDeployBlockedReason(selectedDeployEpoch) && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                      <XCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{getDeployBlockedReason(selectedDeployEpoch)}</span>
                    </div>
                  )}
                  
                  {/* Deployer Info (when epoch selected) */}
                  {selectedDeployEpoch && deployerInfo && canDeployEpoch(selectedDeployEpoch) && (
                    <div className="mt-3 p-2 bg-white border border-amber-200 rounded text-xs">
                      <p className="font-medium text-amber-800">Send 0.005 BNB for gas to:</p>
                      <p className="font-mono text-[#f0b90b] break-all">{deployerInfo.address}</p>
                      <p className="text-[#888] mt-1">Current: {deployerInfo.currentBalance?.toFixed(4) || '0'} BNB</p>
                    </div>
                  )}
                  
                  {/* Wallet Warning */}
                  {!isTreasuryWallet && (
                    <Alert className="mt-3 border-amber-500/20 bg-amber-100">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700 text-sm">
                        Connect treasury wallet to deploy/fund
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              
              {epochs.length === 0 ? (
                <div className="p-12 text-center">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-[#ccc]" />
                  <p className="text-[#666]">No epochs yet. Finalize your first epoch when ready.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#eee]">
                  {/* Show limited epochs or all based on state */}
                  {(showAllEpochs ? epochs : epochs.slice(0, EPOCHS_TO_SHOW)).map((epoch) => (
                    <div key={epoch.epochId} className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <span className="text-lg font-bold">Epoch #{epoch.epochId}</span>
                          {getStatusBadge(epoch.status)}
                          {getFlowPathBadge(epoch.epochId)}
                        </div>
                        <span className="text-lg font-mono text-[#f0b90b]">
                          {epoch.status === 'COLLECTING' ? 'Collecting...' : `${formatNumber(epoch.totalRewardPool || 0)} BNB`}
                        </span>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-[#888]">Participants:</span>
                          <span className="ml-2 font-medium">{epoch.totalParticipants || 0}</span>
                        </div>
                        <div>
                          <span className="text-[#888]">Merkle Root:</span>
                          <span className="ml-2 font-mono text-xs">
                            {epoch.merkleRoot ? `${epoch.merkleRoot.slice(0, 10)}...` : 'Not generated'}
                          </span>
                        </div>
                        <div>
                          <span className="text-[#888]">Distributor:</span>
                          {epoch.distributorAddress ? (
                            <a 
                              href={`https://bscscan.com/address/${epoch.distributorAddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 text-[#f0b90b] hover:underline font-mono text-xs"
                            >
                              {epoch.distributorAddress.slice(0, 10)}...
                              <ExternalLink className="w-3 h-3 inline ml-1" />
                            </a>
                          ) : (
                            <span className="ml-2 text-[#888]">Not deployed</span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {/* Indicator for epochs needing deployment (use dropdown above) */}
                        {epoch.merkleRoot && !epoch.distributorAddress && (
                          <Badge 
                            className="bg-amber-100 text-amber-700 border-amber-300 cursor-pointer hover:bg-amber-200"
                            onClick={() => setSelectedDeployEpochId(String(epoch.epochId))}
                          >
                            <Rocket className="w-3 h-3 mr-1" />
                            Needs deployment — click to select
                          </Badge>
                        )}

                        {/* Step 2: Fund Distributor */}
                        {epoch.distributorAddress && epoch.status === 'FINALIZED' && (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              placeholder={epoch.totalRewardPool || 'Amount'}
                              value={fundAmount}
                              onChange={(e) => setFundAmount(e.target.value)}
                              className="w-32 h-9 bg-[#d3cec6] border-[#b8b3ab]"
                            />
                            <Button
                              onClick={() => handleFundDistributor(epoch)}
                              disabled={funding || isPending || isConfirming || !isConnected || !isTreasuryWallet}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              {(funding || isPending || isConfirming) ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Funding...</>
                              ) : (
                                <><Upload className="w-4 h-4 mr-2" /> Fund</>
                              )}
                            </Button>
                          </div>
                        )}

                        {/* Step 3: Return Unclaimed */}
                        {epoch.status === 'CLAIMABLE' && epoch.distributorAddress && (
                          <Button
                            onClick={() => handleReturnUnclaimed(epoch.distributorAddress)}
                            disabled={isPending || isConfirming}
                            variant="outline"
                            className="border-[#b8b3ab]"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Return Unclaimed
                          </Button>
                        )}
                      </div>

                      {/* Wallet Warning - only for funding (deploy warning is in top section) */}
                      {epoch.distributorAddress && epoch.status === 'FINALIZED' && !isTreasuryWallet && (
                        <Alert className="mt-3 border-amber-500/20 bg-amber-500/10">
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                          <AlertDescription className="text-amber-700 text-sm">
                            Connect treasury wallet to fund distributor
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  ))}
                  
                  {/* Show More / Show Less button */}
                  {epochs.length > EPOCHS_TO_SHOW && (
                    <div className="p-4 text-center border-t border-[#eee]">
                      <Button
                        variant="ghost"
                        onClick={() => setShowAllEpochs(!showAllEpochs)}
                        className="text-[#888] hover:text-[#1a1a1a]"
                      >
                        {showAllEpochs ? (
                          <>Show Less (3 most recent)</>
                        ) : (
                          <>Show All {epochs.length} Epochs ({epochs.length - EPOCHS_TO_SHOW} hidden)</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        ) : activeTab === 'verification' ? (
          /* VERIFICATION TAB - ENHANCED */
          <div className="space-y-4">
            {/* Instructions Alert */}
            <Alert className="border-amber-500/20 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-[#f9a825]" />
              <AlertDescription className="text-[#f9a825]">
                <strong>X/Twitter:</strong> Check that the verification code is posted publicly on their timeline or profile.
                <br />
                <strong>YouTube:</strong> Check that the code is in their Channel About section OR a video description (NOT comments).
              </AlertDescription>
            </Alert>
            
            {/* Data Export Section */}
            <div className="p-4 bg-[#e8e3db] rounded-xl border border-[#d4cfc4]">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="font-semibold text-[#1a1a1a]">Database Export</h3>
                  <p className="text-sm text-[#666]">Download creator identity data (includes email)</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => window.open(`${BACKEND_URL}/api/admin/export/creatorIdentities.xlsx`, '_blank')}
                    variant="outline"
                    size="sm"
                    className="border-green-500/30 text-green-700 hover:bg-green-500/10"
                    data-testid="export-xlsx-button"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Download XLSX
                  </Button>
                  <Button 
                    onClick={() => window.open(`${BACKEND_URL}/api/admin/export/creatorIdentities.json`, '_blank')}
                    variant="outline"
                    size="sm"
                    className="border-blue-500/30 text-blue-700 hover:bg-blue-500/10"
                    data-testid="export-json-button"
                  >
                    <FileJson className="w-4 h-4 mr-2" />
                    Download JSON
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Queue Header with Filter */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-bold text-[#1a1a1a]">Verification Queue</h2>
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-sm">
                    {platformCounts.all} Total
                  </Badge>
                  <Badge variant="outline" className="text-sm bg-blue-500/10 text-blue-600 border-blue-500/20">
                    <Twitter className="w-3 h-3 mr-1" /> {platformCounts.twitter}
                  </Badge>
                  <Badge variant="outline" className="text-sm bg-red-500/10 text-red-600 border-red-500/20">
                    <Youtube className="w-3 h-3 mr-1" /> {platformCounts.youtube}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Select value={platformFilter} onValueChange={setPlatformFilter}>
                  <SelectTrigger className="w-[180px] bg-[#d3cec6] border-[#b8b3ab]">
                    <SelectValue placeholder="Filter by platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms ({platformCounts.all})</SelectItem>
                    <SelectItem value="twitter">
                      <div className="flex items-center gap-2">
                        <Twitter className="w-4 h-4" /> X/Twitter ({platformCounts.twitter})
                      </div>
                    </SelectItem>
                    <SelectItem value="youtube">
                      <div className="flex items-center gap-2">
                        <Youtube className="w-4 h-4" /> YouTube ({platformCounts.youtube})
                      </div>
                    </SelectItem>
                    <SelectItem value="tiktok">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" /> TikTok ({platformCounts.tiktok})
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {filteredQueue.length === 0 ? (
              <Card className="p-12 text-center bg-[#e8e3db]/50 border-[#b8b3ab]">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h3 className="text-xl font-bold mb-2 text-[#1a1a1a]">All Clear!</h3>
                <p className="text-[#666]">
                  {platformFilter === 'all' 
                    ? 'No pending verifications' 
                    : `No pending ${platformFilter} verifications`}
                </p>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredQueue.map((item, index) => {
                  const verificationUrl = getVerificationUrl(item);
                  const displayId = getDisplayIdentifier(item);
                  const canonicalId = getCanonicalIdentifier(item);
                  
                  return (
                    <Card key={`${item.wallet}-${index}`} className="p-6 bg-[#e8e3db]/50 border-[#b8b3ab]" data-testid="verification-item">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Wallet & Platform */}
                          <div className="flex items-center gap-3 mb-3 flex-wrap">
                            <span className="font-mono text-sm text-[#333] truncate">{item.wallet}</span>
                            <Badge className={getPlatformBadgeColor(item.platform)}>
                              {getPlatformIcon(item.platform)}
                              <span className="ml-1 capitalize">{item.platform}</span>
                            </Badge>
                            {item.status && (
                              <Badge variant="outline" className="text-xs">
                                {item.status}
                              </Badge>
                            )}
                            {/* YouTube Check Status Badge */}
                            {item.platform === 'youtube' && getYouTubeCheckStatusBadge(item)}
                          </div>
                          
                          {/* Details Grid */}
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">
                                {item.platform === 'youtube' ? 'Channel' : item.platform === 'tiktok' ? 'Username' : 'Handle'}
                              </p>
                              <p className="text-sm font-medium truncate text-[#f0b90b]">{displayId}</p>
                            </div>
                            
                            {canonicalId && (
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Channel ID</p>
                                <p className="text-sm font-mono text-xs truncate">{canonicalId}</p>
                              </div>
                            )}
                            
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Verification Code</p>
                              <p className="text-sm font-mono bg-[#d3cec6] px-2 py-1 rounded truncate">
                                {item.verificationCode}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Submitted</p>
                              <p className="text-sm">
                                {item.submittedAt 
                                  ? new Date(item.submittedAt).toLocaleString()
                                  : 'Unknown'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Original URL if available */}
                          {item.metadata?.originalUrl && (
                            <div className="mb-4">
                              <p className="text-xs text-slate-500 mb-1">Submitted URL</p>
                              <p className="text-sm text-[#666] truncate">{item.metadata.originalUrl}</p>
                            </div>
                          )}
                          
                          {/* YouTube Check Result Details */}
                          {item.platform === 'youtube' && item.checkStatus === 'FOUND' && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <CheckCheck className="w-4 h-4 text-green-600" />
                                <span className="text-sm font-semibold text-green-700">
                                  Code Found: {item.foundWhere === 'ABOUT' ? 'Channel About Section' : 'Video Description'}
                                </span>
                              </div>
                              {item.foundUrl && (
                                <a 
                                  href={item.foundUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-green-600 hover:underline flex items-center gap-1"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  View proof location
                                </a>
                              )}
                              {item.checkedAt && (
                                <p className="text-xs text-green-600 mt-1">
                                  Checked: {new Date(item.checkedAt).toLocaleString()}
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* YouTube Check Error Details */}
                          {item.platform === 'youtube' && item.checkStatus === 'ERROR' && item.checkError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-700">
                                <AlertTriangle className="w-4 h-4 inline mr-1" />
                                Check failed: {item.checkError}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2 shrink-0">
                          {/* YouTube Check Button */}
                          {item.platform === 'youtube' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleYouTubeCheck(item)}
                              disabled={checking === item.wallet}
                              className="border-[#b8b3ab] bg-blue-50 hover:bg-blue-100"
                              data-testid={`check-${item.wallet}`}
                            >
                              {checking === item.wallet ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <><Search className="w-4 h-4 mr-1" /> Check</>
                              )}
                            </Button>
                          )}
                          
                          {verificationUrl && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(verificationUrl, '_blank')}
                              className="border-[#b8b3ab]"
                              data-testid={`view-profile-${item.wallet}`}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View Profile
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            onClick={() => handleVerify(item.wallet, item.platform, true, item)}
                            disabled={processing === item.wallet}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid={`approve-${item.wallet}`}
                          >
                            {processing === item.wallet ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              const reason = prompt('Rejection reason (optional):');
                              if (reason !== null) {
                                handleVerify(item.wallet, item.platform, false, item, reason || 'Code not found');
                              }
                            }}
                            disabled={processing === item.wallet}
                            data-testid={`reject-${item.wallet}`}
                          >
                            {processing === item.wallet ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><XCircle className="w-4 h-4 mr-1" /> Reject</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeTab === 'settings' ? (
          /* SETTINGS TAB */
          <div className="space-y-6">
            <Card className="p-6 bg-white border-[#b8b3ab]">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-4">TIP Token Price</h2>
              <p className="text-[#666] text-sm mb-4">
                Set the TIP token price in USD. This is used to display the treasury value on the rewards page.
                If TIP is listed on DEXScreener or CoinGecko, the price will be fetched automatically.
              </p>
              
              {currentPrice && (
                <div className="bg-[#f0b90b]/10 rounded-xl p-4 mb-4">
                  <p className="text-sm text-[#666]">Current Price</p>
                  <p className="text-2xl font-bold text-[#f0b90b]">${currentPrice.toFixed(6)} USD</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="e.g. 0.05"
                  value={tipPrice}
                  onChange={(e) => setTipPrice(e.target.value)}
                  className="flex-1 bg-[#d4cfc4] border-[#b8b3ab]"
                />
                <Button
                  onClick={handleSavePrice}
                  disabled={savingPrice}
                  className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white px-6"
                >
                  {savingPrice ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Price'}
                </Button>
              </div>
            </Card>

            {/* TRUE Points Scoring Settings - V2 Configuration */}
            <ScoringConfigV2 adminWallet={address} getAuthHeaders={getAuthHeaders} />

            {/* Scout Program Settings */}
            <ScoutSettingsPanel getAuthHeaders={getAuthHeaders} />

            {/* Admin Security - Wallet-based Password */}
            <Card className="p-6 bg-white border-[#b8b3ab]">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#f0b90b]" />
                Admin Security
              </h2>
              <p className="text-[#666] text-sm mb-4">
                Secure your admin panel with wallet-based authentication. Only the linked wallet can change the password.
              </p>
              
              {/* Current Status */}
              <div className="bg-[#f5f3f0] rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[#888]">Admin Wallet Status</p>
                    {adminSettings?.adminWalletConfigured ? (
                      <p className="text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Configured: {adminSettings.adminWallet}
                      </p>
                    ) : (
                      <p className="text-amber-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Not configured - using default password
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Connect Wallet First */}
              {!isConnected ? (
                <div className="text-center py-6 bg-[#f5f3f0] rounded-xl">
                  <Wallet className="w-10 h-10 text-[#888] mx-auto mb-3" />
                  <p className="text-[#666] mb-3">Connect your wallet to manage admin security</p>
                  <Button onClick={openConnectModal} className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white">
                    Connect Wallet
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 bg-[#f5f3f0] rounded-lg">
                    <p className="text-xs text-[#888]">Connected Wallet</p>
                    <p className="font-mono text-sm text-[#1a1a1a]">{address}</p>
                  </div>
                  
                  {/* Setup or Change Password Form */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-[#666] block mb-1">New Password</label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password (min 6 characters)"
                        className="bg-[#d4cfc4] border-[#b8b3ab]"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#666] block mb-1">Confirm Password</label>
                      <Input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="bg-[#d4cfc4] border-[#b8b3ab]"
                      />
                    </div>
                    
                    {!adminSettings?.adminWalletConfigured ? (
                      <Button
                        onClick={handleSetupWallet}
                        disabled={settingUpWallet || !newPassword || !confirmPassword}
                        className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
                      >
                        {settingUpWallet ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up...</>
                        ) : (
                          <>
                            <Shield className="w-4 h-4 mr-2" />
                            Setup Admin Wallet & Password
                          </>
                        )}
                      </Button>
                    ) : adminSettings?.adminWallet?.toLowerCase()?.includes(address?.toLowerCase()?.slice(2, 12)) ? (
                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !newPassword || !confirmPassword}
                        className="w-full bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
                      >
                        {changingPassword ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                        ) : (
                          <>
                            <Shield className="w-4 h-4 mr-2" />
                            Change Password (Sign with Wallet)
                          </>
                        )}
                      </Button>
                    ) : (
                      <Alert className="border-red-500/20 bg-red-500/10">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-700">
                          This wallet is not the admin wallet. Connect with {adminSettings?.adminWallet} to change password.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                  
                  <p className="text-xs text-[#888] mt-2">
                    {adminSettings?.adminWalletConfigured 
                      ? "Password changes require signing a message with your admin wallet - no gas fees required."
                      : "Setting up links this wallet as admin. You'll sign a message to prove ownership - no gas fees."}
                  </p>
                </div>
              )}
            </Card>

            <Card className="p-6 bg-white border-[#b8b3ab]">
              <h2 className="text-lg font-bold text-[#1a1a1a] mb-4">Contract Addresses</h2>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between p-3 bg-[#f5f3f0] rounded-lg">
                  <div>
                    <span className="text-[#888]">TIP Token</span>
                    <p className="font-mono text-[#1a1a1a] text-xs">{TIP_TOKEN_ADDRESS}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(TIP_TOKEN_ADDRESS)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#f5f3f0] rounded-lg">
                  <div>
                    <span className="text-[#888]">Treasury</span>
                    <p className="font-mono text-[#1a1a1a] text-xs">{TREASURY_ADDRESS}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(TREASURY_ADDRESS)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#f5f3f0] rounded-lg">
                  <span className="text-[#888]">Network</span>
                  <span className="text-[#1a1a1a] font-medium">BNB Chain (BSC Mainnet)</span>
                </div>
              </div>
            </Card>

            {/* Old Contracts - Collapsible Dropdown */}
            {oldContracts.length > 0 && (
              <Card className="p-6 bg-white border-[#b8b3ab]">
                <button
                  onClick={() => setOldContractsOpen(!oldContractsOpen)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="old-contracts-toggle"
                >
                  <div>
                    <h2 className="text-lg font-bold text-[#1a1a1a]">Old Distributor Contracts</h2>
                    <p className="text-sm text-[#666]">Recover unclaimed BNB from expired contracts</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#888] bg-[#f5f3f0] px-2 py-1 rounded">{oldContracts.length} contracts</span>
                    <ChevronDown className={`w-5 h-5 text-[#888] transition-transform ${oldContractsOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                
                {oldContractsOpen && (
                  <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
                    {oldContracts.map((contract) => (
                      <div key={contract.address} className="p-3 bg-[#f5f3f0] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-xs text-[#666]">{contract.address.slice(0, 10)}...{contract.address.slice(-8)}</span>
                          <span className={`text-sm font-medium ${contract.balance > 0 ? 'text-green-600' : 'text-[#888]'}`}>
                            {contract.balance.toFixed(6)} BNB
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#888]">
                            {contract.deadlinePassed ? (
                              <span className="text-green-600">✓ Deadline passed</span>
                            ) : (
                              `Deadline: ${new Date(contract.claimDeadline * 1000).toLocaleDateString()}`
                            )}
                          </span>
                          
                          {contract.canReturn ? (
                            <Button
                              size="sm"
                              onClick={() => handleReturnUnclaimed(contract.address)}
                              disabled={returningFunds === contract.address}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            >
                              {returningFunds === contract.address ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Returning...</>
                              ) : (
                                'Return to Treasury'
                              )}
                            </Button>
                          ) : contract.balance > 0 ? (
                            <span className="text-xs text-amber-600">Wait for deadline</span>
                          ) : (
                            <span className="text-xs text-[#888]">Empty</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            <Alert className="border-amber-500/20 bg-amber-500/10">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-700">
                <strong>Important:</strong> To deploy distributors and fund epochs, you must connect with the treasury wallet.
              </AlertDescription>
            </Alert>
          </div>
        ) : activeTab === 'qr' ? (
          /* QR CODES TAB */
          <div className="space-y-6">
            {/* QR Stats Grid */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="p-4 bg-[#e8e3db] border-[#b8b3ab]">
                <p className="text-xs text-[#666] mb-1">Total QR Codes</p>
                <p className="text-2xl font-bold text-[#1a1a1a]" data-testid="qr-total">{qrStats?.total || 0}</p>
              </Card>
              <Card className="p-4 bg-[#e8e3db] border-[#b8b3ab]">
                <p className="text-xs text-[#666] mb-1">Active</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="qr-active">{qrStats?.active || 0}</p>
              </Card>
              <Card className="p-4 bg-[#e8e3db] border-[#b8b3ab]">
                <p className="text-xs text-[#666] mb-1">Inactive</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="qr-inactive">{qrStats?.inactive || 0}</p>
              </Card>
              <Card className="p-4 bg-[#e8e3db] border-[#b8b3ab]">
                <p className="text-xs text-[#666] mb-1">Revoked</p>
                <p className="text-2xl font-bold text-red-600" data-testid="qr-revoked">{qrStats?.revoked || 0}</p>
              </Card>
            </div>
            
            {/* QR Reputation Config */}
            <Card className="p-6 bg-[#e8e3db] border-[#b8b3ab]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#1a1a1a]">
                  <Settings2 className="w-5 h-5 inline mr-2" />
                  QR Reputation Config
                </h3>
                <Button 
                  onClick={handleSaveQrConfig} 
                  disabled={savingQrConfig}
                  className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white"
                  data-testid="save-qr-config-btn"
                >
                  {savingQrConfig ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Config
                </Button>
              </div>
              
              {qrConfig ? (
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-[#666]">Tipper Baseline</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={qrConfig.tipperBaseline || 0.6}
                      onChange={(e) => setQrConfig({...qrConfig, tipperBaseline: parseFloat(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666]">Creator Baseline</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={qrConfig.creatorBaseline || 0.7}
                      onChange={(e) => setQrConfig({...qrConfig, creatorBaseline: parseFloat(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666]">Days Window</label>
                    <Input
                      type="number"
                      value={qrConfig.daysWindow || 30}
                      onChange={(e) => setQrConfig({...qrConfig, daysWindow: parseInt(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666]">Tier 2 Threshold</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={qrConfig.tier2Threshold || 0.75}
                      onChange={(e) => setQrConfig({...qrConfig, tier2Threshold: parseFloat(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666]">Tier 3 Threshold</label>
                    <Input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={qrConfig.tier3Threshold || 0.9}
                      onChange={(e) => setQrConfig({...qrConfig, tier3Threshold: parseFloat(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#666]">Epochs Normalizer</label>
                    <Input
                      type="number"
                      value={qrConfig.epochsNormalizer || 10}
                      onChange={(e) => setQrConfig({...qrConfig, epochsNormalizer: parseInt(e.target.value)})}
                      className="mt-1 bg-[#d4cfc4] border-[#b8b3ab]"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-[#666]">Loading config...</p>
              )}
            </Card>
            
            {/* Debug Wallet */}
            <Card className="p-6 bg-[#e8e3db] border-[#b8b3ab]">
              <h3 className="text-lg font-bold text-[#1a1a1a] mb-4">
                <Search className="w-5 h-5 inline mr-2" />
                Debug Wallet Reputation
              </h3>
              <div className="flex gap-2 mb-4">
                <Input
                  placeholder="0x..."
                  value={debugWallet}
                  onChange={(e) => setDebugWallet(e.target.value)}
                  className="flex-1 bg-[#d4cfc4] border-[#b8b3ab]"
                />
                <Button onClick={handleDebugWallet} className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white">
                  <Eye className="w-4 h-4 mr-2" />
                  Debug
                </Button>
              </div>
              
              {qrWalletDebug && (
                <div className="bg-[#d4cfc4] rounded-lg p-4 space-y-3">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#666]">Wallet</p>
                      <p className="font-mono text-sm">{qrWalletDebug.wallet}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">Current Epoch</p>
                      <p className="font-bold">{qrWalletDebug.currentEpochId || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-4 pt-2 border-t border-[#b8b3ab]">
                    <div>
                      <p className="text-xs text-[#666]">R_tipper</p>
                      <p className="text-lg font-bold text-emerald-600">{qrWalletDebug.multipliers?.rTipper?.toFixed(4) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">R_creator</p>
                      <p className="text-lg font-bold text-blue-600">{qrWalletDebug.multipliers?.rCreator?.toFixed(4) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">Tier</p>
                      <p className="text-lg font-bold">{qrWalletDebug.multipliers?.tier || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">Active Days</p>
                      <p className="text-lg font-bold">{qrWalletDebug.inputs?.distinctActiveDays || 0}</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
            
            {/* QR Preview for Epoch */}
            <Card className="p-6 bg-[#e8e3db] border-[#b8b3ab]">
              <h3 className="text-lg font-bold text-[#1a1a1a] mb-4">
                <Shield className="w-5 h-5 inline mr-2" />
                QR Finalization Preview
              </h3>
              <div className="flex gap-2 flex-wrap">
                {epochs.slice(0, 5).map(epoch => (
                  <Button 
                    key={epoch.epochId}
                    variant="outline"
                    onClick={() => handleQrPreview(epoch.epochId)}
                    className="border-[#b8b3ab]"
                    disabled={qrPreview?.loading}
                  >
                    {qrPreview?.loading && qrPreview?.epochId === epoch.epochId ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Preview Epoch {epoch.epochId}
                  </Button>
                ))}
              </div>
              
              {qrPreview && !qrPreview.loading && (
                <div className="mt-4 bg-[#d4cfc4] rounded-lg p-4">
                  <div className="grid md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-[#666]">Epoch</p>
                      <p className="font-bold">{qrPreview.epochId}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">QR Wallets</p>
                      <p className="font-bold">{qrPreview.totalWallets || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">Avg R_tipper</p>
                      <p className="font-bold text-emerald-600">{qrPreview.avgRTipper?.toFixed(4) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#666]">Avg R_creator</p>
                      <p className="font-bold text-blue-600">{qrPreview.avgRCreator?.toFixed(4) || 'N/A'}</p>
                    </div>
                  </div>
                  {qrPreview.message && (
                    <p className="text-sm text-[#666]">{qrPreview.message}</p>
                  )}
                </div>
              )}
            </Card>
            
            {/* QR List */}
            <Card className="p-6 bg-[#e8e3db] border-[#b8b3ab]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#1a1a1a]">
                  <QrCode className="w-5 h-5 inline mr-2" />
                  Registered QR Codes
                </h3>
                <Button 
                  onClick={loadQrData} 
                  variant="outline" 
                  size="sm"
                  className="border-[#b8b3ab]"
                  disabled={qrLoading}
                >
                  {qrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>
              
              {qrList.length === 0 ? (
                <p className="text-[#666] text-center py-8">No QR codes registered yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#d4cfc4]">
                      <tr>
                        <th className="text-left p-3">QR ID</th>
                        <th className="text-left p-3">Wallet</th>
                        <th className="text-left p-3">Label</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Created</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qrList.map((qr) => (
                        <tr key={qr.qrId} className="border-b border-[#d4cfc4]">
                          <td className="p-3 font-mono text-xs">{qr.qrId}</td>
                          <td className="p-3 font-mono text-xs">
                            <a 
                              href={`https://bscscan.com/address/${qr.wallet}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {qr.wallet?.slice(0, 6)}...{qr.wallet?.slice(-4)}
                            </a>
                          </td>
                          <td className="p-3">{qr.label || '-'}</td>
                          <td className="p-3">
                            <Badge className={
                              qr.status === 'active' ? 'bg-emerald-500' :
                              qr.status === 'inactive' ? 'bg-amber-500' :
                              'bg-red-500'
                            }>
                              {qr.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-xs text-[#666]">
                            {qr.createdAt ? new Date(qr.createdAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(`/tip/${qr.qrId}`, '_blank')}
                                title="View tip page"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                              {qr.status !== 'revoked' && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleToggleQrStatus(qr.qrId, qr.status)}
                                    title={qr.status === 'active' ? 'Deactivate' : 'Activate'}
                                  >
                                    {qr.status === 'active' ? <XCircle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-emerald-500" />}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRevokeQr(qr.qrId)}
                                    disabled={revokingQr === qr.qrId}
                                    title="Revoke (permanent)"
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    {revokingQr === qr.qrId ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
};
